import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth, api } from '../context/AuthContext';

const StyledAppBar = styled(AppBar)`
  background: rgba(25, 118, 210, 0.95);
  backdrop-filter: blur(8px);
`;

// This would be replaced with proper cart state management in a full implementation
// You could use Context API, Redux, or localStorage for this
const useCart = () => {
  const [cart, setCart] = useState({});

  // Load cart from localStorage on component mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Calculate total items in cart
  const getTotalItems = () => {
    return Object.values(cart).reduce((sum, quantity) => sum + quantity, 0);
  };

  return { cart, getTotalItems };
};

const Navbar = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState(null);
  const [userMemorial, setUserMemorial] = useState(null);
  
  // Get cart information
  const { getTotalItems } = useCart();
  
  // Check if user is admin (moved to a single declaration)
  const isAdmin = auth.user && auth.user.role === 'admin';
  
  // Debug admin status
  useEffect(() => {
    console.log('Auth user:', auth.user);
    console.log('Is admin?', isAdmin);
  }, [auth.user, isAdmin]);

  // Check if user has a memorial when they're authenticated
  useEffect(() => {
    const checkUserMemorial = async () => {
      if (auth.isAuthenticated) {
        try {
          const response = await api.get('/api/memorials/user/has-memorial');
          if (response.data.hasMemorial) {
            setUserMemorial(response.data.memorialId);
          } else {
            setUserMemorial(null);
          }
        } catch (error) {
          console.error('Error checking memorial status:', error);
        }
      }
    };

    if (auth.isAuthenticated) {
      checkUserMemorial();
    } else {
      setUserMemorial(null);
    }
  }, [auth.isAuthenticated]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAdminMenu = (event) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminClose = () => {
    setAdminAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const handleMemorialAction = () => {
    if (userMemorial) {
      navigate(`/memorial/${userMemorial}`);
    } else {
      navigate('/create-memorial');
    }
    handleClose();
  };

  const handleEditMemorial = () => {
    navigate(`/edit-memorial/${userMemorial}`);
    handleClose();
  };

  const handleScanQR = () => {
    navigate('/scan');
    handleClose();
  };

  const handleCartClick = () => {
    navigate('/checkout');
  };

  const handleShopClick = () => {
    navigate('/#shop-section');
    // Optional: scroll to shop section if already on home page
    const shopSection = document.getElementById('shop-section');
    if (shopSection) {
      shopSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <StyledAppBar position="sticky" elevation={4}>
      <Container maxWidth="lg">
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{ 
              flexGrow: 1, 
              color: 'white',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
            onClick={() => navigate('/')}
          >
            MemoryLink
          </Typography>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
            <Button 
              sx={{ color: 'white' }}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            
            <Button 
              sx={{ color: 'white' }}
              onClick={() => navigate('/about')}
            >
              About
            </Button>
            
            <Button 
              sx={{ color: 'white' }}
              onClick={handleShopClick}
            >
              Shop
            </Button>
            
            <Button 
              sx={{ color: 'white' }}
              startIcon={<QrCodeScannerIcon />}
              onClick={handleScanQR}
            >
              Scan QR Code
            </Button>
            
            {auth.isAuthenticated ? (
              <>
                <Button 
                  sx={{ color: 'white' }}
                  onClick={handleMemorialAction}
                >
                  {userMemorial ? 'View Memorial' : 'Create Memorial'}
                </Button>
                
                {userMemorial && (
                  <Button 
                    sx={{ color: 'white' }}
                    onClick={handleEditMemorial}
                  >
                    Edit Memorial
                  </Button>
                )}
                
                <Button 
                  sx={{ color: 'white' }}
                  onClick={() => navigate('/dashboard')}
                >
                  Dashboard
                </Button>

                {isAdmin && (
                  <>
                    <Button
                      sx={{ color: 'white' }}
                      startIcon={<AdminPanelSettingsIcon />}
                      onClick={handleAdminMenu}
                    >
                      Admin
                    </Button>
                    <Menu
                      anchorEl={adminAnchorEl}
                      open={Boolean(adminAnchorEl)}
                      onClose={handleAdminClose}
                    >
                      <MenuItem onClick={() => { navigate('/admin/qrcodes'); handleAdminClose(); }}>
                        <QrCodeIcon sx={{ mr: 1 }} />
                        QR Code List
                      </MenuItem>
                      <MenuItem onClick={() => { navigate('/admin/qrcodes/generate'); handleAdminClose(); }}>
                        <QrCodeIcon sx={{ mr: 1 }} />
                        Generate QR Codes
                      </MenuItem>
                      <MenuItem onClick={() => { navigate('/admin/products'); handleAdminClose(); }}>
                        <ShoppingBasketIcon sx={{ mr: 1 }} />
                        Manage Products
                      </MenuItem>
                    </Menu>
                  </>
                )}
                
                <Button 
                  variant="contained" 
                  color="secondary"
                  onClick={handleLogout}
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: '#e0e0e0',
                    }
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                variant="contained" 
                color="secondary"
                onClick={() => navigate('/login')}
                sx={{ 
                  bgcolor: 'white', 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: '#e0e0e0',
                  }
                }}
              >
                Login
              </Button>
            )}

            {/* Cart Icon */}
            <Tooltip title="View Cart">
              <IconButton 
                color="inherit" 
                onClick={handleCartClick}
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={getTotalItems()} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
            {/* Cart Icon for Mobile */}
            <Tooltip title="View Cart">
              <IconButton 
                color="inherit" 
                onClick={handleCartClick}
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={getTotalItems()} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <IconButton
              sx={{ color: 'white' }}
              onClick={handleMenu}
            >
              <MenuIcon />
            </IconButton>
          </Box>
          
          {/* Mobile Menu Items - FIXED to avoid fragments */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => { navigate('/'); handleClose(); }}>
              Home
            </MenuItem>
            
            <MenuItem onClick={() => { navigate('/about'); handleClose(); }}>
              About
            </MenuItem>
            
            <MenuItem onClick={() => { handleShopClick(); handleClose(); }}>
              Shop
            </MenuItem>
            
            <MenuItem onClick={handleScanQR}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <QrCodeScannerIcon sx={{ mr: 1 }} />
                Scan QR Code
              </Box>
            </MenuItem>
            
            {/* Authentication-dependent items */}
            {auth.isAuthenticated && (
              <MenuItem onClick={handleMemorialAction}>
                {userMemorial ? 'View Memorial' : 'Create Memorial'}
              </MenuItem>
            )}
            
            {auth.isAuthenticated && userMemorial && (
              <MenuItem onClick={handleEditMemorial}>
                Edit Memorial
              </MenuItem>
            )}
            
            {auth.isAuthenticated && (
              <MenuItem onClick={() => { navigate('/dashboard'); handleClose(); }}>
                Dashboard
              </MenuItem>
            )}

            {/* Admin items */}
            {auth.isAuthenticated && isAdmin && <Divider />}
            
            {auth.isAuthenticated && isAdmin && (
              <MenuItem 
                sx={{ color: 'primary.main', fontWeight: 'bold' }}
                disabled
              >
                <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                Admin Controls
              </MenuItem>
            )}
            
            {auth.isAuthenticated && isAdmin && (
              <MenuItem onClick={() => { navigate('/admin/qrcodes'); handleClose(); }}>
                <QrCodeIcon sx={{ mr: 1 }} />
                QR Code List
              </MenuItem>
            )}
            
            {auth.isAuthenticated && isAdmin && (
              <MenuItem onClick={() => { navigate('/admin/qrcodes/generate'); handleClose(); }}>
                <QrCodeIcon sx={{ mr: 1 }} />
                Generate QR Codes
              </MenuItem>
            )}
            
            {auth.isAuthenticated && isAdmin && (
              <MenuItem onClick={() => { navigate('/admin/products'); handleClose(); }}>
                <ShoppingBasketIcon sx={{ mr: 1 }} />
                Manage Products
              </MenuItem>
            )}
            
            {auth.isAuthenticated && isAdmin && <Divider />}
            
            {auth.isAuthenticated && (
              <MenuItem onClick={handleLogout}>
                Logout
              </MenuItem>
            )}
            
            {!auth.isAuthenticated && (
              <MenuItem onClick={() => { navigate('/login'); handleClose(); }}>
                Login
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar;